import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { PaletteMode } from '@mui/material'

interface ColorModeContextType {
  mode: PaletteMode
  toggleColorMode: () => void
}

export const ColorModeContext = createContext<ColorModeContextType>({
  mode: 'light',
  toggleColorMode: () => {},
})

export const ColorModeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setMode] = useState<PaletteMode>(() => {
    const stored = localStorage.getItem('colorMode')
    return stored === 'dark' ? 'dark' : 'light'
  })

  useEffect(() => {
    document.documentElement.dataset.theme = mode
  }, [mode])

  const value = useMemo<ColorModeContextType>(
    () => ({
      mode,
      toggleColorMode: () => {
        setMode((prev) => {
          const next = prev === 'light' ? 'dark' : 'light'
          return next
        })
      },
    }),
    [mode]
  )

  useEffect(() => {
    localStorage.setItem('colorMode', mode)
  }, [mode])

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>
}

export const useColorMode = () => useContext(ColorModeContext)
